import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readZellDetails } from "@/office/excel/analyse";
import { nichtPruefbar } from "../schnittstelle";

export const lektionRahmen: Lesson = {
  id: "rahmen",
  title: "Rahmenlinien und der Formatpinsel",
  summary: "Linien, die gliedern statt einsperren – und Formate in Sekunden übertragen.",
  durationMinutes: 8,
  learningGoals: [
    "Rahmenlinien gezielt für Kopf- und Ergebniszeilen setzen",
    "Die Gitternetzlinien von Rahmenlinien unterscheiden",
    "Formate mit dem Formatpinsel übertragen",
  ],
  steps: [
    {
      id: "rahmenlinien",
      title: "Rahmenlinien setzen",
      explanation: [
        "Die grauen Gitternetzlinien werden nicht gedruckt – sie sind nur eine Hilfe am " +
          "Bildschirm. Was auf dem Papier eine Linie haben soll, braucht eine Rahmenlinie: " +
          "„Start“ → Schaltfläche „Rahmenlinien“ oder Strg + 1 → „Rahmen“.",
        "Weniger ist mehr: Eine Linie unter der Kopfzeile und eine über der Ergebniszeile " +
          "gliedern eine Liste besser als ein Gitter um jede Zelle. Ein komplettes Gitter " +
          "entsteht ohnehin, wenn man den Bereich als Tabelle formatiert (Strg + T) und " +
          "eine passende Tabellenformatvorlage wählt.",
        "Die doppelte untere Rahmenlinie ist die klassische Markierung einer Summe.",
      ],
      keyPoints: [
        "Gitternetzlinien werden nicht gedruckt, Rahmenlinien schon.",
        "Linie unter der Kopfzeile, Linie über der Summe.",
        "Strg + 1 → Rahmen für Linienart und Farbe.",
      ],
      task: {
        instruction:
          "Gib der Kopfzeile einer Liste (mindestens drei Zellen nebeneinander) eine untere Rahmenlinie.",
        hint: "Kopfzeile markieren, Start → Rahmenlinien → Rahmenlinie unten.",
        check: async () => {
          const details = await readZellDetails();
          if (details === null) {
            return nichtPruefbar(
              "Die Rahmenlinien einzelner Zellen",
              "1.9",
              "Prüfe in der Seitenansicht (Strg + P), ob die Linie gedruckt würde.",
              true,
            );
          }
          const zeilen = new Map<string, number>();
          for (const zelle of details.filter((d) => d.rahmen.unten)) {
            const zeile = `${zelle.sheetName}!${zelle.address.replace(/^[A-Z]+/, "")}`;
            zeilen.set(zeile, (zeilen.get(zeile) ?? 0) + 1);
          }
          const beste = [...zeilen.entries()].sort((a, b) => b[1] - a[1])[0];
          return fromCriteria(
            [
              criterion(
                beste ? `Zeile ${beste[0]}: ${beste[1]} Zellen mit unterer Linie` : "Mindestens drei Zellen einer Zeile mit unterer Rahmenlinie",
                (beste?.[1] ?? 0) >= 3,
              ),
            ],
            {
              success: "Die Kopfzeile ist abgesetzt – auch auf dem Ausdruck.",
              failure: "Es gibt noch keine Zeile mit durchgehender unterer Rahmenlinie.",
              hint: "Eine Tabellenformatvorlage färbt die Kopfzeile, setzt aber nicht zwingend eine Rahmenlinie.",
            },
          );
        },
      },
    },
    {
      id: "formatpinsel",
      title: "Der Formatpinsel",
      explanation: [
        "„Format übertragen“ – der Pinsel auf der Registerkarte „Start“ – überträgt " +
          "Zahlenformat, Schrift, Füllung, Rahmen und Ausrichtung einer Zelle auf andere. " +
          "Doppelklick hält ihn aktiv, Esc beendet.",
        "Auch ganze Spaltenbreiten lassen sich so übertragen: Spaltenkopf markieren, Pinsel, " +
          "Zielspaltenkopf anklicken. Und wie in Word gilt: Wer immer wieder dieselbe " +
          "Kombination überträgt, legt eine Zellformatvorlage an (Modul „Zellformatvorlagen " +
          "und Layout“).",
      ],
      keyPoints: [
        "Pinsel: einmal übertragen, Doppelklick: mehrfach.",
        "Spaltenkopf → Pinsel → Spaltenkopf überträgt die Breite.",
        "Wiederkehrendes als Zellformatvorlage.",
      ],
    },
  ],
};
